import React, { useState } from 'react';
import {
  Button,
  TextField,
  Alert,
  CircularProgress
} from '@mui/material';
import AppDialog, { AppDialogTitle, AppDialogContent, AppDialogActions } from '../layout/AppDialog';
import { adminResetUserPassword } from '../../services/api';

const ResetPasswordAdminModal = ({ open, onClose, user, onPasswordReset }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await adminResetUserPassword(user.id, newPassword);
      setSuccess(`Password for ${user.username} has been reset successfully.`);
      setNewPassword('');
      setConfirmPassword('');
      if (onPasswordReset) {
        onPasswordReset();
      }
      setTimeout(() => { // Keep success message visible for a bit before closing
        onClose();
        setSuccess(''); // Clear success message after closing
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  // Reset fields when modal opens or user changes
  React.useEffect(() => {
    if (open) {
      setNewPassword('');
      setConfirmPassword('');
      setError('');
      setSuccess('');
    }
  }, [open, user]);


  if (!user) return null;

  return (
    <AppDialog open={open} onClose={onClose}>
      <AppDialogTitle onClose={onClose} subtitle={`Set a new password for ${user.username}`}>
        Reset Password
      </AppDialogTitle>
      <form onSubmit={handleSubmit}>
        <AppDialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          
          <TextField
            required
            fullWidth
            margin="normal"
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={loading || !!success}
          />
          <TextField
            required
            fullWidth
            margin="normal"
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading || !!success}
          />
        </AppDialogContent>
        <AppDialogActions>
          <Button onClick={onClose} disabled={loading || !!success} variant="outlined" color="inherit">Cancel</Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading || !newPassword || !confirmPassword || !!success}
          >
            {loading ? <CircularProgress size={24} /> : 'Reset Password'}
          </Button>
        </AppDialogActions>
      </form>
    </AppDialog>
  );
};

export default ResetPasswordAdminModal;
